import { BaseStorage } from './StorageInterface'
import type { Document, DocumentInfo, SearchResult, StorageOptions } from './types'

/**
 * IndexedDB-based storage for web platform
 * Single Responsibility: Handle browser-based document storage
 */
export class WebStorage extends BaseStorage {
  private db: IDBDatabase | null = null
  private dbName = 'KoreanGrammarEditor'
  private dbVersion = 1
  private isInitialized = false
  
  constructor(options: StorageOptions = {}) {
    super(options)
  }
  
  /**
   * Initialize IndexedDB
   */
  async init(): Promise<void> {
    if (this.isInitialized) return
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion)
      
      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'))
      }
      
      request.onsuccess = () => {
        this.db = request.result
        this.isInitialized = true
        resolve()
      }
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        
        // Create documents store
        if (!db.objectStoreNames.contains('documents')) {
          const store = db.createObjectStore('documents', { keyPath: 'id' })
          store.createIndex('title', 'title', { unique: false })
          store.createIndex('updatedAt', 'updatedAt', { unique: false })
          store.createIndex('tags', 'metadata.tags', { unique: false, multiEntry: true })
        }
        
        // Create metadata store for quick listing
        if (!db.objectStoreNames.contains('metadata')) {
          const metaStore = db.createObjectStore('metadata', { keyPath: 'id' })
          metaStore.createIndex('updatedAt', 'updatedAt', { unique: false })
        }
      }
    })
  }
  
  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.init()
    }
  }
  
  /**
   * Save document to IndexedDB
   */
  async save(id: string, content: string, metadata?: Partial<Document['metadata']>): Promise<void> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    // Check size limit
    const sizeInBytes = new Blob([content]).size
    if (sizeInBytes > this.options.maxSizePerDoc!) {
      throw new Error(`Document exceeds maximum size of ${this.options.maxSizePerDoc! / 1024 / 1024}MB`)
    }
    
    const now = new Date()
    // Use title from metadata if provided, otherwise extract from content
    const title = (metadata as any)?.title || this.extractTitle(content)
    
    // Create document
    const document: Document = {
      id: id || this.generateId(),
      title,
      content,
      metadata: {
        wordCount: this.calculateWordCount(content),
        characterCount: this.calculateCharacterCount(content),
        lastSaved: now,
        ...metadata
      },
      createdAt: now,
      updatedAt: now
    }
    
    // Check if document exists
    const existing = await this.exists(id)
    if (existing) {
      const existingDoc = await this.load(id)
      document.createdAt = existingDoc.createdAt
    }
    
    // Save to IndexedDB
    const transaction = this.db.transaction(['documents', 'metadata'], 'readwrite')
    
    // Save full document
    const docStore = transaction.objectStore('documents')
    await this.promisifyRequest(docStore.put(document))
    
    // Save metadata for quick access
    const metaStore = transaction.objectStore('metadata')
    const metaData: DocumentInfo = {
      id: document.id,
      title: document.title,
      preview: this.extractPreview(content),
      createdAt: document.createdAt,
      updatedAt: document.updatedAt,
      wordCount: document.metadata.wordCount,
      tags: document.metadata.tags
    }
    await this.promisifyRequest(metaStore.put(metaData))
    
    // Emit save event
    this.emit({ type: 'save', documentId: id })
  }
  
  /**
   * Load document from IndexedDB
   */
  async load(id: string): Promise<Document> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['documents'], 'readonly')
    const store = transaction.objectStore('documents')
    const request = store.get(id)
    
    const document = await this.promisifyRequest<Document>(request)
    
    if (!document) {
      throw new Error(`Document ${id} not found`)
    }
    
    return document
  }
  
  /**
   * Delete document from IndexedDB
   */
  async delete(id: string): Promise<void> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['documents', 'metadata'], 'readwrite')
    
    // Delete from documents store
    const docStore = transaction.objectStore('documents')
    await this.promisifyRequest(docStore.delete(id))
    
    // Delete from metadata store
    const metaStore = transaction.objectStore('metadata')
    await this.promisifyRequest(metaStore.delete(id))
    
    // Emit delete event
    this.emit({ type: 'delete', documentId: id })
  }
  
  /**
   * List all documents (metadata only for performance)
   */
  async list(): Promise<DocumentInfo[]> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['metadata'], 'readonly')
    const store = transaction.objectStore('metadata')
    const index = store.index('updatedAt')
    const request = index.getAll()
    
    const documents = await this.promisifyRequest<DocumentInfo[]>(request)
    
    // Sort by updatedAt (newest first)
    return documents.sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )
  }
  
  /**
   * Search documents
   */
  async search(query: string): Promise<SearchResult[]> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const queryLower = query.toLowerCase()
    const results: SearchResult[] = []
    
    // Get all documents (for now, simple search)
    const transaction = this.db.transaction(['documents'], 'readonly')
    const store = transaction.objectStore('documents')
    const request = store.getAll()
    
    const documents = await this.promisifyRequest<Document[]>(request)
    
    // Search in title and content
    for (const doc of documents) {
      const titleMatch = doc.title.toLowerCase().includes(queryLower)
      const contentMatch = doc.content.toLowerCase().includes(queryLower)
      
      if (titleMatch || contentMatch) {
        // Calculate simple relevance score
        let score = 0
        if (titleMatch) score += 2
        if (contentMatch) score += 1
        
        // Find matching lines for preview
        const lines = doc.content.split('\n')
        const matchingLines = lines.filter(line => 
          line.toLowerCase().includes(queryLower)
        ).slice(0, 3)
        
        results.push({
          documentId: doc.id,
          title: doc.title,
          preview: matchingLines.join(' ... ') || this.extractPreview(doc.content),
          score,
          matches: matchingLines
        })
      }
    }
    
    // Sort by relevance score
    return results.sort((a, b) => b.score - a.score)
  }
  
  /**
   * Search by tags
   */
  async searchByTags(tags: string[]): Promise<DocumentInfo[]> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const results: DocumentInfo[] = []
    const transaction = this.db.transaction(['metadata'], 'readonly')
    const store = transaction.objectStore('metadata')
    const index = store.index('tags')
    
    // Search for each tag
    for (const tag of tags) {
      const request = index.getAll(tag)
      const docs = await this.promisifyRequest<DocumentInfo[]>(request)
      results.push(...docs)
    }
    
    // Remove duplicates
    const uniqueResults = new Map<string, DocumentInfo>()
    results.forEach(doc => uniqueResults.set(doc.id, doc))
    
    return Array.from(uniqueResults.values())
  }
  
  /**
   * Check if document exists
   */
  async exists(id: string): Promise<boolean> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['documents'], 'readonly')
    const store = transaction.objectStore('documents')
    const request = store.count(id)
    
    const count = await this.promisifyRequest<number>(request)
    return count > 0
  }
  
  /**
   * Get document info without loading content
   */
  async getInfo(id: string): Promise<DocumentInfo | null> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['metadata'], 'readonly')
    const store = transaction.objectStore('metadata')
    const request = store.get(id)
    
    const info = await this.promisifyRequest<DocumentInfo>(request)
    return info || null
  }
  
  /**
   * Delete all documents
   */
  async deleteAll(): Promise<void> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['documents', 'metadata'], 'readwrite')
    
    // Clear documents store
    const docStore = transaction.objectStore('documents')
    await this.promisifyRequest(docStore.clear())
    
    // Clear metadata store
    const metaStore = transaction.objectStore('metadata')
    await this.promisifyRequest(metaStore.clear())
  }
  
  /**
   * Export all documents
   */
  async exportAll(): Promise<Document[]> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['documents'], 'readonly')
    const store = transaction.objectStore('documents')
    const request = store.getAll()
    
    return await this.promisifyRequest<Document[]>(request)
  }
  
  /**
   * Import documents
   */
  async importDocuments(documents: Document[]): Promise<void> {
    await this.ensureInitialized()
    
    if (!this.db) throw new Error('Database not initialized')
    
    const transaction = this.db.transaction(['documents', 'metadata'], 'readwrite')
    const docStore = transaction.objectStore('documents')
    const metaStore = transaction.objectStore('metadata')
    
    for (const doc of documents) {
      // Save document
      await this.promisifyRequest(docStore.put(doc))
      
      // Save metadata
      const metaData: DocumentInfo = {
        id: doc.id,
        title: doc.title,
        preview: this.extractPreview(doc.content),
        createdAt: doc.createdAt,
        updatedAt: doc.updatedAt,
        wordCount: doc.metadata.wordCount,
        tags: doc.metadata.tags
      }
      await this.promisifyRequest(metaStore.put(metaData))
    }
  }
  
  /**
   * Helper to promisify IndexedDB requests
   */
  private promisifyRequest<T>(request: IDBRequest): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }
  
  
  /**
   * Extract preview from content
   */
  protected extractPreview(content: string): string {
    if (!content) return ''
    
    // Get first 200 characters
    const preview = content.substring(0, 200).replace(/\n/g, ' ').trim()
    return preview.length === 200 ? preview + '...' : preview
  }
  
  /**
   * Calculate word count
   */
  protected calculateWordCount(content: string): number {
    if (!content) return 0
    // Count Korean characters and English words
    const koreanChars = (content.match(/[\u3131-\uD79D]/g) || []).length
    const englishWords = (content.match(/[a-zA-Z]+/g) || []).length
    return koreanChars + englishWords
  }
  
  /**
   * Calculate character count
   */
  protected calculateCharacterCount(content: string): number {
    return content ? content.length : 0
  }
}